import { FleetTruckLocation } from '@numeo/types'
import { ELDType } from '@numeo/types-core'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from 'store'
import { TruckMarker } from '../pages/dashboard/pages/dispatcher/pages/dispatcher-dashboard/components/map/types'
import { eldApi } from '../services/eldApi'
import { resolveFleetTruckConnected } from '../utils/fleet-truck-status'

// ELD provider colors
const ELD_PROVIDER_COLORS: Partial<Record<ELDType, string>> = {
    [ELDType.TtEld]: '#3388FF',
    [ELDType.AtEld]: '#00A86B',
    [ELDType.LucidEld]: '#33cc33',
    [ELDType.Samsara]: '#33aaff',
    [ELDType.Motive]: '#ff9933',
    [ELDType.SwiftEld]: '#14A37F',
    [ELDType.GreenLightEld]: '#9C27B0',
    [ELDType.QuantumEld]: '#9C27B0',
    [ELDType.HOSconnect]: '#FF5722',
    [ELDType.PrestigeEld]: '#00BCD4',
    [ELDType.FactorEld]: '#E91E63',
    [ELDType.DailyPlusEld]: '#4CAF50',
    [ELDType.OrientEld]: '#FF9800',
    [ELDType.OntimeEld]: '#795548',
    [ELDType.EvoEld]: '#3F51B5',
    [ELDType.FiveEld]: '#00897B',
    [ELDType.ZippyEld]: '#F4511E',
    [ELDType.NoorEld]: '#3F51B5',
    [ELDType.DsgEld]: '#4489CC',
    [ELDType.SpectreEld]: '#607D8B',
    [ELDType.NewWorldEld]: '#fff',
    [ELDType.WheelsElogEld]: '#7A2CC8',
    [ELDType.LevelEld]: '#607D8B',
    [ELDType.PandaEld]: '#6b8e23',
    [ELDType.LogitexEld]: '#1f58d6',
    [ELDType.BlueHorseEld]: '#1E3A8A',
    [ELDType.ForzaEld]: '#D32F2F',
    [ELDType.RockEld]: '#F97316',
    [ELDType.LeaderEld]: '#7C3AED',
    [ELDType.TeletracEld]: '#0EA5E9',
}

// Transform FleetTruckLocation to TruckMarker
const transformToTruckMarker = (fleetTruck: FleetTruckLocation): TruckMarker => {
    const timestamp = fleetTruck.timestamp ? new Date(fleetTruck.timestamp).getTime() : Date.now()
    const connected = resolveFleetTruckConnected(fleetTruck)

    return {
        id: fleetTruck.appTruckId || fleetTruck.id,
        type: fleetTruck.eldType,
        name: fleetTruck.name || fleetTruck.truck?.mainInfo || 'Unknown',
        vin: fleetTruck.truck?.vin || fleetTruck.id,
        position: {
            lat: fleetTruck.coordinates?.lat || 0,
            lng: fleetTruck.coordinates?.lng || 0,
        },
        timestamp,
        connected,
        color: ELD_PROVIDER_COLORS[fleetTruck.eldType] || '#1976d2',
        status: connected ? 'online' : 'offline',
        speed: fleetTruck.speed,
        heading: fleetTruck.headingDegrees,
        rotation: fleetTruck.headingDegrees,
        fuelLevelPercent: fleetTruck.fuelLevelPercent,
        data: {
            truck: fleetTruck.truck,
            eldCompany: fleetTruck.eldCompany,
            locationAddress: fleetTruck.locationAddress,
            motionStatus: fleetTruck.motionStatus,
        },
    }
}

interface FetchTrucksError {
    message: string
    details?: string | null
    timestamp?: number
    code?: string
}

// Async thunk for fetching trucks from all ELD providers using unified fleet API
export const fetchTrucks = createAsyncThunk<TruckMarker[], void, { rejectValue: FetchTrucksError }>(
    'truck/fetchTrucks',
    async (_, { rejectWithValue, getState }) => {
        try {
            // Get application integrations from Redux state
            const state = getState() as RootState
            const application = state.auth.application
            const eldIntegrations = application?.integrations?.eldIntegrations || []

            // Check if application has any ELD integrations
            if (eldIntegrations.length === 0) {
                console.warn('[truckSlice] No ELD integrations found')
                return []
            }

            // Single API call to get all fleet truck locations
            const response = await eldApi.getFleetTrucksLocations()

            if (!response.success) {
                return rejectWithValue({
                    message: 'Failed to fetch fleet truck locations',
                    details: 'API returned unsuccessful response',
                    timestamp: Date.now(),
                    code: 'API_ERROR',
                })
            }

            if (!response.data?.trucks || response.data.trucks.length === 0) {
                console.warn('[truckSlice] No trucks returned from fleet API')
                return []
            }

            // Transform FleetTruckLocation[] to TruckMarker[]
            const trucks = response.data.trucks.map(transformToTruckMarker)

            return trucks
        } catch (error) {
            const err = error as Error

            console.error('[truckSlice] Error fetching trucks:', err)

            return rejectWithValue({
                message: err.message || 'Failed to fetch truck data',
                details: err.stack,
                timestamp: Date.now(),
                code: 'FETCH_ERROR',
            })
        }
    }
)

interface TruckState {
    trucks: TruckMarker[]
    loading: boolean
    error: string | null
    errorDetails?: string | null
    lastUpdated?: number
}

const initialState: TruckState = {
    trucks: [],
    loading: false,
    error: null,
    errorDetails: null,
    lastUpdated: undefined,
}

const truckSlice = createSlice({
    name: 'truck',
    initialState,
    reducers: {
        updateTruckState: (state, action: PayloadAction<TruckMarker>) => {
            const index = state.trucks.findIndex((truck) => truck.id === action.payload.id)
            if (index !== -1) {
                state.trucks[index] = action.payload
            } else {
                state.trucks.push(action.payload)
            }
            state.lastUpdated = Date.now()
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
            state.loading = false
        },
        setErrorWithDetails: (state, action: PayloadAction<{ message: string; details?: null }>) => {
            state.error = action.payload.message
            state.errorDetails = action.payload.details
            state.loading = false
        },
        clearError: (state) => {
            state.error = null
            state.errorDetails = null
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTrucks.pending, (state) => {
                state.loading = true
                state.error = null
                state.errorDetails = null
            })
            .addCase(fetchTrucks.fulfilled, (state, action) => {
                state.loading = false
                state.trucks = action.payload
                state.error = null
                state.errorDetails = null
                state.lastUpdated = Date.now()
            })
            .addCase(fetchTrucks.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload?.message || 'An error occurred while fetching truck data'
                state.errorDetails = action.payload?.details || action.error.message
                state.lastUpdated = Date.now()

                console.error('[truckSlice] Fetch trucks rejected:', {
                    message: state.error,
                    details: state.errorDetails,
                })
            })
    },
})

// Selectors
export const selectTrucks = (state: RootState) => state.truck.trucks
export const selectTrucksLoading = (state: RootState) => state.truck.loading
export const selectTrucksError = (state: RootState) => state.truck.error

// Reducer
export default truckSlice.reducer
